import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, CheckCircle, ChevronDown, ChevronRight, X } from 'lucide-react';
import { TriageItem, TriageAlternative } from '../data/mockData';
interface TriagePanelProps {
  items: TriageItem[];
  selectedDriverId: string | null;
  onSelectDriver: (driverId: string) => void;
  onContactDriver?: (driverId: string) => void;
}
export function TriagePanel({
  items: initialItems,
  selectedDriverId,
  onSelectDriver,
  onContactDriver
}: TriagePanelProps) {
  const [items, setItems] = useState<TriageItem[]>(initialItems);
  const [filter, setFilter] = useState<'all' | 'urgent' | 'proactive' | 'resolved'>('all');
  const handleResolve = (id: string, resolution: 'approved' | 'dismissed') => {
    setItems(prev => prev.map(item => item.id === id ? {
      ...item,
      status: resolution
    } : item));
  };
  // Compute filter counts
  const triageCounts = {
    all: items.filter(i => i.status === 'pending').length,
    urgent: items.filter(i => i.status === 'pending' && i.priority <= 2).length,
    proactive: items.filter(i => i.status === 'pending' && i.category === 'proactive').length,
    resolved: items.filter(i => i.status !== 'pending').length
  };
  const filteredItems = items.filter(item => {
    if (filter === 'all') return item.status === 'pending';
    if (filter === 'urgent') return item.status === 'pending' && item.priority <= 2;
    if (filter === 'proactive') return item.status === 'pending' && item.category === 'proactive';
    if (filter === 'resolved') return item.status !== 'pending';
    return true;
  });
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    return a.priority - b.priority;
  });
  return <div className="flex flex-col h-full" data-id="element-692">
      {/* Filter Header */}
      <div className="p-3 border-b border-slate-800 shrink-0" data-id="element-693">
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-694">
          <FilterTab active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={triageCounts.all} data-id="element-695" />
          <FilterTab active={filter === 'urgent'} onClick={() => setFilter('urgent')} label="Urgent" count={triageCounts.urgent} data-id="element-696" />
          <FilterTab active={filter === 'proactive'} onClick={() => setFilter('proactive')} label="Proactive" count={triageCounts.proactive} data-id="element-697" />
          <FilterTab active={filter === 'resolved'} onClick={() => setFilter('resolved')} label="Resolved" count={triageCounts.resolved} data-id="element-698" />
        </div>
      </div>

      {/* Triage List */}
      <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-699">
        <AnimatePresence mode="popLayout" data-id="element-700">
          {sortedItems.length === 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="text-center p-8 text-slate-500 text-sm flex flex-col items-center" data-id="element-701">
              <Sparkles className="w-8 h-8 mb-3 opacity-20" data-id="element-702" />
              <p data-id="element-703">No triage items match your filters.</p>
              <p className="text-xs mt-1 opacity-60" data-id="element-704">You're all caught up!</p>
            </motion.div>}
          {sortedItems.map(item => <TriageCard key={item.id} item={item} isSelected={item.driverId === selectedDriverId} onClick={() => onSelectDriver(item.driverId)} onResolve={resolution => handleResolve(item.id, resolution)} onContactDriver={() => onContactDriver?.(item.driverId)} data-id="element-705" />)}
        </AnimatePresence>
      </div>
    </div>;
}
function FilterTab({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return <button onClick={onClick} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium flex items-center justify-center gap-1.5
        ${active ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-706">
      {label}
      {count !== undefined && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-707">
          {count}
        </span>}
    </button>;
}
function TriageCard({
  item,
  isSelected,
  onClick,
  onResolve,
  onContactDriver
}: {
  item: TriageItem;
  isSelected: boolean;
  onClick: () => void;
  onResolve: (res: 'approved' | 'dismissed') => void;
  onContactDriver?: () => void;
}) {
  const [showAlternatives, setShowAlternatives] = useState(false);
  const isResolved = item.status !== 'pending';
  if (isResolved) {
    return <motion.div layout initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="relative p-3 mb-3 last:mb-0 rounded-xl border border-slate-800/50 bg-slate-900/30 opacity-60 flex items-center gap-3" data-id="element-708">
        <CheckCircle className="w-5 h-5 text-brand-green shrink-0" data-id="element-709" />
        <div className="flex-1 min-w-0" data-id="element-710">
          <p className="text-xs text-slate-400 truncate" data-id="element-711">
            <span className="font-semibold text-slate-300" data-id="element-712">Resolved:</span>{' '}
            {item.issue}
          </p>
        </div>
      </motion.div>;
  }
  const priorityColor = item.priority === 1 ? 'bg-brand-red' : item.priority === 2 ? 'bg-brand-amber' : 'bg-brand-blue';
  return <motion.div layout initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    layout: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  }} onClick={onClick} className={`relative p-4 mb-3 last:mb-0 rounded-xl border cursor-pointer transition-all overflow-hidden
        ${isSelected ? 'bg-slate-800/60 border-slate-600 shadow-[0_0_15px_rgba(0,0,0,0.2)]' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}
      `} data-id="element-713">
      {/* Priority Indicator */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${priorityColor}`} data-id="element-714" />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 pl-1" data-id="element-715">
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border
          ${item.category === 'proactive' ? 'text-brand-blue border-brand-blue/30 bg-brand-blue/10' : item.priority === 1 ? 'text-brand-red border-brand-red/30 bg-brand-red/10' : item.priority === 2 ? 'text-brand-amber border-brand-amber/30 bg-brand-amber/10' : 'text-brand-blue border-brand-blue/30 bg-brand-blue/10'}
        `} data-id="element-716">
          {item.category === 'proactive' ? 'PROACTIVE' : 'ALERT RESPONSE'}
        </span>
      </div>

      {/* Issue Summary */}
      <p className="text-sm font-medium text-slate-200 mb-3 pl-1 leading-snug" data-id="element-717">
        {item.issue}
      </p>

      {/* AI Recommendation Box */}
      <div className="bg-brand-blue/10 border border-brand-blue/20 rounded-lg p-3 mb-3 relative overflow-hidden" data-id="element-718">
        <div className="absolute top-0 right-0 p-2 opacity-10 pointer-events-none" data-id="element-719">
          <Sparkles className="w-12 h-12 text-brand-blue" data-id="element-720" />
        </div>
        <div className="flex items-start gap-2 relative z-10" data-id="element-721">
          <Sparkles className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" data-id="element-722" />
          <div data-id="element-723">
            <span className="text-[10px] font-bold text-brand-blue uppercase tracking-wider block mb-1" data-id="element-724">
              Recommendation
            </span>
            <p className="text-xs text-slate-300 leading-relaxed" data-id="element-725">
              {item.recommendation}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pl-1" data-id="element-726">
        <button onClick={e => {
        e.stopPropagation();
        onResolve('approved');
        const label = (item.actions[0]?.label || '').toLowerCase();
        if (label.includes('contact') || item.actionHint?.toLowerCase().includes('contact')) {
          onContactDriver?.();
        }
      }} className="flex-1 bg-brand-green hover:bg-brand-green/90 text-slate-950 text-xs font-bold py-2 rounded-md transition-colors" data-id="element-727">
          {item.actions[0]?.label || 'Approve'}
        </button>
        {item.alternatives.length > 0 && <button onClick={e => {
        e.stopPropagation();
        setShowAlternatives(!showAlternatives);
      }} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-md transition-colors flex items-center gap-1" data-id="element-728">
            Modify{' '}
            <ChevronDown className={`w-3 h-3 transition-transform ${showAlternatives ? 'rotate-180' : ''}`} data-id="element-729" />
          </button>}
        <button onClick={e => {
        e.stopPropagation();
        onResolve('dismissed');
      }} className="px-2 py-2 text-slate-500 hover:text-slate-400 transition-colors" title="Dismiss" data-id="element-730">
          <X className="w-4 h-4" data-id="element-731" />
        </button>
      </div>

      {/* Alternatives Expansion */}
      <AnimatePresence data-id="element-732">
        {showAlternatives && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-733">
            <div className="pt-3 mt-3 border-t border-slate-800/50 space-y-2 pl-1" data-id="element-734">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-735">
                Alternative Actions
              </p>
              {item.alternatives.map(alt => <button key={alt.id} onClick={e => {
            e.stopPropagation();
            onResolve('approved');
          }} className="w-full text-left p-2 rounded-md border border-slate-800 hover:border-slate-600 bg-slate-900/50 hover:bg-slate-800 transition-colors group" data-id="element-736">
                  <div className="flex items-center justify-between mb-0.5" data-id="element-737">
                    <span className="text-xs font-medium text-slate-300 group-hover:text-slate-200" data-id="element-738">
                      {alt.label}
                    </span>
                    <ChevronRight className="w-3 h-3 text-slate-600 group-hover:text-slate-400" data-id="element-739" />
                  </div>
                  <span className="text-[10px] text-slate-500 block" data-id="element-740">
                    {alt.description}
                  </span>
                </button>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}